import * as XLSX from "xlsx";
import { ExcludeMember } from "../models/campaign";

const COL_CONTACT = "Customer Contact";
const COL_EMAIL = "Email Address";

/** Downloads a valid example xlsx template for the exclude member upload. */
export const downloadExcludeTemplate = () => {
  const ws = XLSX.utils.aoa_to_sheet([
    [COL_CONTACT, COL_EMAIL],
    ["6591234567", "example@email.com"],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, "ExampleFormatExclude.xlsx");
};

export type ExcludeParseResult = {
  /** Normalized rows with exactly "Customer Contact" and "Email Address" keys. */
  rows: Record<string, any>[];
  /** Structured members (phone number / email), used for the payload. */
  members: ExcludeMember[];
};

/** Finds the first column whose header matches the given pattern. */
const findKey = (keys: string[], pattern: RegExp): string | undefined =>
  keys.find((k) => pattern.test(k));

/**
 * Parses an uploaded .xlsx/.xls file into preview rows and a deduped list of
 * structured exclude members. Detects a phone/contact column and an email
 * column independently, so the template can carry either or both.
 */
export const parseExcludeMemberFile = async (
  file: File,
): Promise<ExcludeParseResult> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  if (!sheet) return { rows: [], members: [] };

  const parsed = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
    defval: "",
  });

  // Drop fully empty rows.
  const rawRows = parsed.filter((row) =>
    Object.values(row).some((v) => String(v ?? "").trim() !== ""),
  );

  const keys = rawRows.length ? Object.keys(rawRows[0]) : [];
  const phoneKey = findKey(keys, /contact|phone|mobile|number/i);
  const emailKey = findKey(keys, /e-?mail/i);

  const seen = new Set<string>();
  const rows: Record<string, any>[] = [];
  const members: ExcludeMember[] = [];

  rawRows.forEach((row) => {
    const phone = phoneKey ? String(row[phoneKey] ?? "").trim() : "";
    const email = emailKey ? String(row[emailKey] ?? "").trim() : "";

    if (!phone && !email) return;

    const dedupeKey = `${phone.toLowerCase()}|${email.toLowerCase()}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    rows.push({ [COL_CONTACT]: phone, [COL_EMAIL]: email });
    members.push({
      ...(phone ? { PhoneNumber: phone } : {}),
      ...(email ? { Email: email } : {}),
    });
  });

  return { rows, members };
};
